import type { MachinePublicHydrated } from "@aura-frontend/omnibus-az-client";
import { useEffect, useMemo } from "react";
import { Provider, useDispatch } from "react-redux";
import { Outlet, useLoaderData } from "react-router-dom";

import { isPopulatedArray } from "@/pages/Tagging/utils/array";

import {
  MagnusSocketStatus,
  SocketProvider,
} from "../Tagging/components/SocketBroker";
import {
  setSessionType,
  setStatTrackingMode,
} from "../Tagging/store/slices/session";
import {
  setCurrentFilter,
  setTrackingState,
} from "../Tagging/store/slices/socket";
import { setActiveView } from "../Tagging/store/slices/view";
import { createTaggingStore } from "../Tagging/store/store";

type LoaderData = {
  machine: MachinePublicHydrated;
};

/**
 * Initializes Challenge Mode state inside the Redux Provider.
 * Sets activeView, filter, and tracking state on mount.
 */
const ChallengeModeInitializer = ({
  children,
  machine,
}: {
  children: React.ReactNode;
  machine: MachinePublicHydrated;
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setActiveView("Challenge Mode"));
    dispatch(setCurrentFilter("has_pitch_data"));
    dispatch(setTrackingState(true));
    dispatch(setSessionType("game"));
    dispatch(
      setStatTrackingMode(isPopulatedArray(machine.smartAvailableActive)),
    );

    return () => {
      dispatch(setCurrentFilter("all"));
    };
  }, [dispatch, machine.smartAvailableActive]);

  return <>{children}</>;
};

/**
 * ChallengeModeSession wraps all challenge mode routes with:
 * - Redux Provider (isolated tagging store)
 * - Socket Context (WebSocket connection for real-time data)
 * - MagnusSocketStatus (connection indicator in navbar)
 * - Challenge Mode state initialization
 */
const ChallengeModeSession = () => {
  const { machine } = useLoaderData() as LoaderData;

  const store = useMemo(() => {
    return createTaggingStore();
  }, []);

  return (
    <Provider store={store}>
      <SocketProvider>
        <MagnusSocketStatus />
        <ChallengeModeInitializer machine={machine}>
          <Outlet />
        </ChallengeModeInitializer>
      </SocketProvider>
    </Provider>
  );
};

export default ChallengeModeSession;
